/**
 * @module errorMiddleware
 * @description Middleware for handling errors and validations in the Conference Room Designer application.
 */

import { AnyAction, Dispatch } from '@reduxjs/toolkit';
import ReducersIndex from "reduxModules/ducks";
import { getAreaProperty, getCurrentValue2, getArea } from "reduxModules/selectors/containerSelectors";
import { getStoreProp } from "utilities/string";
import toolIndex from "toolConstants";
import { some, has } from "lodash";
import { RootState } from 'types/redux/state';
import { ErrorObject, ErrorMiddleware, ToolErrorConstants } from 'types/middleware/errorMiddleware';
import { showError, showWarning } from 'utilities/toastManager';

/**
 * @function errorMiddleware
 * @description Intercepts Redux actions to handle errors, perform validations, and manage customizations.
 * @param {Object} store - The Redux store.
 * @returns {Function} A middleware function.
 */
const errorMiddleware: ErrorMiddleware = ({ dispatch, getState }) => (next) => (action: AnyAction) => {
  /**
   * @section Pre action items
   * @description All the logic below intercepts the action to prevent somethoing from happening 
   */
  let store: RootState = getState();
  const tool: string = store.container.currentTool;
  const currentDevice: string = getCurrentValue2(store, 'currentDevice');
  const deviceCount: number = getCurrentValue2(store, `${currentDevice}.qty`);

  // Get tool constants
  const toolConstants = toolIndex[tool] as ToolErrorConstants;
  
  // Check table dimensions BEFORE they get clamped in the reducer
  if (action.type === 'container/setTable') {
    // Check if we have size.length or size.width in the payload
    const sizeLength = action.payload?.['size.length'];
    const sizeWidth = action.payload?.['size.width'];
    
    if (sizeLength !== undefined || sizeWidth !== undefined) {
      const table = store.container?.areas?.[store.container?.activeTab]?.table;
      
      if (table?.shape) {
        const tableConstants = toolConstants?.constants?.tables?.[table.shape];
        
        if (tableConstants?.minimum) {
          const minLength = tableConstants.minimum.length;
          const minWidth = tableConstants.minimum.width;
          const isMeter = store.ui?.units === 'meters';
          
          // Check if trying to set values below minimum and show warning
          if (sizeLength !== undefined && sizeLength < minLength) {
            const shapeName = table.shape.charAt(0).toUpperCase() + table.shape.slice(1);
            const minStr = isMeter ? `${(minLength).toFixed(1)}m` : `${Math.round(minLength * 3.28084)}'`;
            showWarning(`${shapeName} table length cannot be smaller than ${minStr}. Setting to minimum.`);
          }
          if (sizeWidth !== undefined && sizeWidth < minWidth && table.shape !== 'round') {
            const shapeName = table.shape.charAt(0).toUpperCase() + table.shape.slice(1);
            const minStr = isMeter ? `${(minWidth).toFixed(1)}m` : `${Math.round(minWidth * 3.28084)}'`;
            showWarning(`${shapeName} table width cannot be smaller than ${minStr}. Setting to minimum.`);
          }
        }
      }
    }
  }

  switch (true) {
    case action.type === ReducersIndex.uiReducers.closeModal.type:
      if (['resetCustomization'].includes(action.payload) && store.ui.errorState) {
        dispatch(ReducersIndex.uiReducers.closeWarning())
      }
      // Clear the 1400 error when the warning modal is closed
      if (action.payload === 'warning' && has(store.ui.error, '1400')) {
        dispatch(ReducersIndex.uiReducers.setError({ code: '1400', msg: null }));
      };
      break; // Add break to prevent warning for fallthrough case
      
    case action.type === ReducersIndex.uiReducers.openModal.type:
      // No automatic modal interception - all warning modals are manual only
      break;

    /**
     * @description conditions to check before the axction is executed and values changes in the store
     */
    // Using action.type.includes instead of direct comparison for action types
    // that might be dynamically defined
    case action.type.includes('deleteSpeaker'):
      const speakerCount: number = (getCurrentValue2(store, 'speakers') || []).length;
      if (speakerCount <= 1) {
        const errorMsg = `Can't delete the last loudspeaker in the area. To remove all loudspeakers, set the area coverage to "None"`;
        // Show error toast to provide immediate feedback
        showError(errorMsg);
        // Still set error state for any components that might use it
        const error: ErrorObject = {
          code: 110,
          msg: errorMsg,
          reset: true,
        };
        dispatch(ReducersIndex.uiReducers.setError({
          code: String(error.code),
          msg: error.msg,
          reset: error.reset,
          params: error.params
        }));
        return;
      };
      break;

    case action.type.includes('addDevice'):
      let maximumCount: number = toolIndex[tool].constants?.tool?.maximumDevices?.[currentDevice] ?? 1;
      let capMessage: string | null = null;

      // PIG-440: ceiling-tile mics (BMA360D variants) have a device-specific hard cap from
      // Dante interface capacity, narrower than the global per-device-type maximum. Apply the
      // smaller of the two so the user can't push past the model's `maxUnits`.
      if (currentDevice === 'microphones') {
        const activeTab = store.container?.activeTab;
        const micModel = store.container?.areas?.[activeTab]?.microphones?.model;
        const ceilingTileSet: Set<string> | undefined =
          (toolIndex[tool] as any)?.constants?.equipment?.micCategories?.CEILING_TILE;
        const micMax: number | undefined =
          (toolIndex[tool] as any)?.constants?.equipment?.microphones?.[micModel]?.maxUnits;
        if (micModel && ceilingTileSet?.has?.(micModel) && typeof micMax === 'number') {
          if (micMax < maximumCount) maximumCount = micMax;
          const modelLabel =
            (toolIndex[tool] as any)?.constants?.equipment?.microphones?.[micModel]?.modelName
            || 'BMA360D';
          capMessage = `${modelLabel} is limited to ${micMax} units per design. Remove a unit to add another.`;
        }
      }

      if (deviceCount >= maximumCount) {
        const warningMsg = capMessage ?? `Can't add more ${currentDevice}.`;
        // Show warning toast instead of error to avoid blocking functionality
        showWarning(warningMsg);
        // DO NOT set error state - this should not block the application
        return; // Block the action without setting error state
      }
      break;

    case action.type.includes('removeDevice'):
      if (deviceCount <= 1) {
        const errorMsg = `Can't delete the last ${currentDevice} in the room.`;
        // Show error toast to provide immediate feedback
        showError(errorMsg);
        // Set error state temporarily
        const error: ErrorObject = {
          code: 130,
          msg: errorMsg,
          reset: true
        };
        dispatch(ReducersIndex.uiReducers.setError({
          code: String(error.code),
          msg: error.msg,
          reset: error.reset,
          params: error.params
        }));
        // Clear error 130 after a delay since it's just a notification
        setTimeout(() => {
          dispatch(ReducersIndex.uiReducers.setError({ code: '130' }));
        }, 2000);
        return;
      }
      break;

    /**
     * @description This last action is a general stopper to reset customization
     * The "localCalc" parameters filters actions so only Warehousse Designer actions go thru here
     * i.e. Both Conference Room Desinger and Classroom Designer don't use this functionality
     * 
     * @note Since this catches any and all "container" actions, it needs to run last 
     * otherwise the previous actions would not run
     */
    case action.type.includes('container'):
      const state = getState();
      const currentArea = state.container?.activeTab;
      const customized = state.container?.areas?.[currentArea]?.customized;
      const localCalc = toolIndex[tool].constants?.tool?.localCalc ?? false;

      if (currentArea && customized && some(customized) && !localCalc) {
        const customReseters = toolIndex[tool].constants.customReseters ?? [];
        const parameter = getStoreProp(action.type);

        if (customReseters.includes(parameter)) {
          const payload = {
            area: currentArea,
            update: {
              parameter: parameter,
              value: action.payload
            },
          };
          // Cast the payload to any to bypass type checking, as the actual Redux action
          // understands this payload structure but TypeScript definition is limited
          dispatch(ReducersIndex.container.setAreaToUpdate(payload as any) as any);

          // Warning toasts are now handled by calculateDevicesMiddleware
          // No need to dispatch error code 100 anymore as it won't trigger any modal
          // and the user feedback is already provided via toast notifications
          
          // prevent further action
          return;
        };
      };
      break;

    default:
      break;
  }

  /**
   * @description run the actions if they have not stopped 
   */
  next(action);
  store = getState();

  /**
   * @section Post action items
   * @description All the logic below happens after the action. Errors will be highlighted and indicated to the user
   * but the user needs to correct them as the value has already been passed to the store 
   */

  /**
   * General Validation
   * @description This checks what parameter in the store is changing.
   * If validation has a function with the same name, it executes that validation 
   */
  const validate = toolConstants?.validate;
  
  switch (true) {
    case action.type === "ui/setUnits":
    // intentional fall through
    case action.type.includes('container'):
      const parameter = getStoreProp(action.type);
      if (validate && parameter in validate) {
        validate[parameter](dispatch as Dispatch<AnyAction>, store);
      };
      break;

    case action.type === ReducersIndex.uiReducers.setOverlaping?.type:
      if (validate?.overlaping) {
        validate.overlaping(dispatch as Dispatch<AnyAction>, store);
      }
      break;

    /**
     * @description Once all validation has gone thru. if any errors are set, the function 
     * below opens the apropiate modal 
     */
    case action.type === ReducersIndex.uiReducers.setError?.type:
      // No automatic modal popups for any errors - all validation errors only show red text
      // The resetCustomization modal (code 100) is now handled by warning toasts in calculateDevicesMiddleware
      // This prevents the edge case where both a toast and blocking modal could appear
      break;

    default:
      break;
  }
};

export default errorMiddleware;
